import React, { useState, useCallback } from 'react';
import { useDropzone, FileRejection } from 'react-dropzone';
import { CircularProgress } from '@mui/material';
import { saveAs } from 'file-saver';

import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import CloudUploadOutlinedIcon from '@mui/icons-material/CloudUploadOutlined';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';

import CreditsAPI from 'api/CreditsAPI';
import InsufficientCredits from 'pages/credit-plans/InsufficientCredits';

// Must match AES_REQUIRED_FIELDS in the analyzer service's
// utils/AesAnalyzer/parse.ts (biteservices).
const REQUIRED_COLUMNS = [
  'License Type Code',
  'Export Control Classification Number',
  'Country of Ultimate Destination Code',
  'Ultimate Consignee Company Name',
  'HTS/Schedule B Number - Full',
  'Commodity Line Value',
];

// AESTIR field names the analyzer reads. The analyzer also accepts common
// platform spellings of each (see its utils/AesAnalyzer/parse.ts); this list is
// only the downloadable template. Headers only — no example row, so a user
// can't upload the template back as if it were real filing data.
const TEMPLATE_HEADERS = [
  'Shipment Reference Number',
  'ITN',
  'Commodity Line Number',
  'USPPI ID Number',
  'USPPI ID Number Type',
  'USPPI Company Name',
  'USPPI City',
  'USPPI State',
  'Filer Type Code',
  'Routed Transaction Indicator',
  'Authorized Agent Company Name',
  'Filing Option Type',
  'EEI Filing Date',
  'Export Date',
  'Country of Ultimate Destination Code',
  'Ultimate Consignee Company Name',
  'Ultimate Consignee Country',
  'Intermediate Consignee Company Name',
  'Intermediate Consignee Country',
  'License Type Code',
  'License Number',
  'License Value',
  'Export Control Classification Number',
  'DDTC USML Category Code',
  'HTS/Schedule B Number - Full',
  'Commodity Description',
  'Quantity 1',
  'Commodity Line Value',
  'Export Information Code',
  'PGA Applicable Indicator',
];

const ACCEPTED_EXTENSIONS = ['csv', 'xlsx', 'xls'];
const MAX_FILE_MB = 50;

interface IRunAesAnalysisModalProps {
  open: boolean;
  handleClose: () => void;
  onAnalyze: (name: string, file: File) => Promise<void>;
  teamId: string;
  creditSpendMode: 'trial' | 'active';
}

const RunAesAnalysisModal: React.FC<IRunAesAnalysisModalProps> = ({
  open,
  handleClose,
  onAnalyze,
  teamId,
  creditSpendMode,
}) => {
  const [analysisName, setAnalysisName] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [uploadError, setUploadError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showInsufficientCredits, setShowInsufficientCredits] = useState(false);

  const handleResetAndClose = () => {
    if (isSubmitting) return;
    setAnalysisName('');
    setSelectedFile(null);
    setUploadError('');
    handleClose();
  };

  const onDrop = useCallback(
    (accepted: File[], rejected: FileRejection[]) => {
      if (rejected.length) {
        setUploadError(rejected[0].errors[0]?.message || 'File rejected.');
        return;
      }
      const file = accepted[0];
      if (!file) return;
      setUploadError('');
      setSelectedFile(file);
      if (!analysisName.trim()) {
        setAnalysisName(file.name.replace(/\.[^.]+$/, ''));
      }
    },
    [analysisName],
  );

  // Extension check in a validator rather than `accept`, so OS-specific
  // spreadsheet MIME types are not silently rejected (same as the Tariff
  // Analyzer modal).
  const fileValidator = (file: File) => {
    const ext = (file.name.split('.').pop() || '').toLowerCase();
    if (!ACCEPTED_EXTENSIONS.includes(ext)) {
      return {
        code: 'file-type-invalid',
        message: 'File type must be .csv or .xlsx',
      };
    }
    if (file.size > MAX_FILE_MB * 1024 * 1024) {
      return {
        code: 'file-too-large',
        message: `File must be under ${MAX_FILE_MB} MB`,
      };
    }
    return null;
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: onDrop,
    validator: fileValidator,
    multiple: false,
  });

  const handleDownloadTemplate = () => {
    const csv = TEMPLATE_HEADERS.join(',');
    saveAs(
      new Blob([`${csv}\n`], { type: 'text/csv;charset=utf-8' }),
      'AES_Analyzer_Template.csv',
    );
  };

  // The exact cost (shipments in the file) is only known once the analyzer
  // has read it, and it fails the run before charging if the balance is short.
  // A team with no credits left at all is stopped here, before the upload.
  const hasAnyCredits = async (): Promise<boolean> => {
    if (!teamId) return true;
    const response =
      creditSpendMode === 'trial'
        ? await CreditsAPI.getTrialCreditsSummary(teamId)
        : await CreditsAPI.getCreditsSummary(teamId);
    if (!response?.success) return true;
    const summary =
      creditSpendMode === 'trial' && Array.isArray(response.result)
        ? response.result[0]
        : response.result;
    return Number(summary?.remaining_credits ?? 0) > 0;
  };

  const handleSubmit = async () => {
    if (!analysisName.trim() || !selectedFile) return;
    setIsSubmitting(true);
    setUploadError('');
    try {
      if (!(await hasAnyCredits())) {
        setShowInsufficientCredits(true);
        return;
      }
      await onAnalyze(analysisName.trim(), selectedFile);
      setAnalysisName('');
      setSelectedFile(null);
    } catch (e: any) {
      setUploadError(
        e?.response?.data?.message || e?.message || 'Upload failed.',
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const isReadyToSubmit =
    !!analysisName.trim() && !!selectedFile && !isSubmitting;

  if (!open) return null;

  return (
    <div
      className='modal-overlay'
      onClick={handleResetAndClose}
      style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
      }}
    >
      <div
        className='modal-cont'
        style={{
          width: '540px',
          maxWidth: '90vw',
          maxHeight: '90vh',
          padding: 0,
          position: 'relative',
          top: 'auto',
          left: 'auto',
          transform: 'none',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className='modal-header-cont-temp'
          style={{
            padding: '24px',
            borderBottom: '1px solid var(--es-soft-border)',
            width: '100%',
            flexShrink: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              width: '100%',
              alignItems: 'flex-start',
            }}
          >
            <div className='modal-header-cont-text'>
              <h1
                className='modal-header-cont-text-h1'
                style={{ fontSize: '20px', marginBottom: '4px' }}
              >
                Run New Analysis
              </h1>
              <p className='modal-header-cont-text-p' style={{ margin: 0 }}>
                Upload an AES / EEI export to audit each commodity line.
              </p>
            </div>
            <div
              className='module-header-btn-close'
              onClick={handleResetAndClose}
              style={{ cursor: 'pointer', color: 'var(--text-secondary)' }}
            >
              <CloseIcon />
            </div>
          </div>
        </div>

        {/* Body */}
        <div
          className='modal-body'
          style={{
            padding: '24px',
            display: 'flex',
            flexDirection: 'column',
            gap: '24px',
            width: '100%',
            boxSizing: 'border-box',
            overflowY: 'auto',
            minHeight: 0,
          }}
        >
          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <label
              style={{
                color: 'var(--text-primary-color)',
                fontSize: '14px',
                fontWeight: 500,
              }}
            >
              Analysis Name
            </label>
            <input
              type='text'
              value={analysisName}
              onChange={(e) => setAnalysisName(e.target.value)}
              placeholder='e.g. Q3 2025 AES Filings'
              maxLength={255}
              style={{
                background: 'var(--bg-colors)',
                color: 'var(--text-primary-color)',
                border: '1px solid var(--es-soft-border)',
                padding: '12px 14px',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                width: '100%',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <div
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              width: '100%',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-end',
              }}
            >
              <label
                style={{
                  color: 'var(--text-primary-color)',
                  fontSize: '14px',
                  fontWeight: 500,
                  margin: 0,
                }}
              >
                AES Export (CSV / XLSX)
              </label>
              <span
                onClick={handleDownloadTemplate}
                style={{
                  color: 'var(--action-color)',
                  fontSize: '13px',
                  fontWeight: 500,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '4px',
                }}
              >
                <FileDownloadOutlinedIcon style={{ fontSize: 16 }} /> Download
                Template
              </span>
            </div>

            {!selectedFile ? (
              <div
                {...getRootProps()}
                style={{
                  border: `2px dashed ${
                    isDragActive
                      ? 'var(--action-color)'
                      : 'var(--es-soft-border)'
                  }`,
                  borderRadius: '8px',
                  padding: '40px 24px',
                  textAlign: 'center',
                  cursor: 'pointer',
                  background: isDragActive
                    ? 'rgba(72, 149, 239, 0.05)'
                    : 'var(--bg-secondary)',
                  transition: 'all 0.2s ease',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <input {...getInputProps()} />
                <CloudUploadOutlinedIcon
                  style={{
                    fontSize: 44,
                    color: 'var(--text-secondary)',
                    marginBottom: '12px',
                  }}
                />
                <p
                  style={{
                    margin: 0,
                    color: 'var(--text-primary-color)',
                    fontWeight: 500,
                    fontSize: '14px',
                  }}
                >
                  <span style={{ color: 'var(--action-color)' }}>
                    Click to upload
                  </span>{' '}
                  or drag and drop
                </p>
                <p
                  style={{
                    margin: '6px 0 0 0',
                    color: 'var(--text-secondary)',
                    fontSize: '13px',
                  }}
                >
                  CSV or XLSX, up to {MAX_FILE_MB} MB
                </p>
              </div>
            ) : (
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  padding: '16px',
                  border: '1px solid var(--es-soft-border)',
                  borderRadius: '8px',
                  background: 'var(--bg-secondary)',
                  width: '100%',
                  boxSizing: 'border-box',
                }}
              >
                <div
                  style={{ display: 'flex', alignItems: 'center', gap: '12px' }}
                >
                  <InsertDriveFileOutlinedIcon
                    style={{ color: 'var(--action-color)', fontSize: 28 }}
                  />
                  <div>
                    <p
                      style={{
                        margin: 0,
                        color: 'var(--text-primary-color)',
                        fontSize: '14px',
                        fontWeight: 500,
                      }}
                    >
                      {selectedFile.name}
                    </p>
                    <p
                      style={{
                        margin: '2px 0 0 0',
                        color: 'var(--text-secondary)',
                        fontSize: '13px',
                      }}
                    >
                      {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                    </p>
                  </div>
                </div>
                <div
                  onClick={() => {
                    if (!isSubmitting) {
                      setSelectedFile(null);
                      setUploadError('');
                    }
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    cursor: isSubmitting ? 'not-allowed' : 'pointer',
                    padding: '8px',
                    borderRadius: '4px',
                    color: 'var(--low-color)',
                  }}
                >
                  <DeleteOutlineIcon fontSize='small' />
                </div>
              </div>
            )}

            {uploadError ? (
              <p
                style={{
                  margin: '4px 0 0 0',
                  color: 'var(--low-color)',
                  fontSize: '13px',
                  fontWeight: 500,
                }}
              >
                {uploadError}
              </p>
            ) : (
              <p
                style={{
                  margin: '4px 0 0 0',
                  color: 'var(--text-secondary)',
                  fontSize: '13px',
                  fontStyle: 'italic',
                }}
              >
                * One row per commodity line. Required columns:{' '}
                {REQUIRED_COLUMNS.join(', ')}. Any other columns are optional —
                extra columns of your own are kept and included in the findings
                export.
              </p>
            )}
            <p
              style={{
                margin: '4px 0 0 0',
                color: 'var(--text-secondary)',
                fontSize: '13px',
              }}
            >
              Uses 1 credit per unique Shipment Reference Number, however many
              lines it has. The file is checked once it is uploaded: if it
              can&apos;t be read, or has more shipments than your team has
              credits, the run fails with the reason and nothing is charged.
            </p>
          </div>
        </div>

        {/* Footer */}
        <div
          style={{
            padding: '24px',
            borderTop: '1px solid var(--es-soft-border)',
            display: 'flex',
            flexDirection: 'row',
            justifyContent: 'flex-end',
            alignItems: 'center',
            gap: '12px',
            width: '100%',
            boxSizing: 'border-box',
            flexShrink: 0,
          }}
        >
          <button
            className='btn-outline'
            onClick={handleResetAndClose}
            disabled={isSubmitting}
            style={{ margin: 0, width: 'auto' }}
          >
            Cancel
          </button>
          <button
            className='btn-primary'
            disabled={!isReadyToSubmit}
            onClick={handleSubmit}
            style={{
              margin: 0,
              width: 'auto',
              opacity: isReadyToSubmit ? 1 : 0.6,
              cursor: isReadyToSubmit ? 'pointer' : 'not-allowed',
            }}
          >
            {isSubmitting ? (
              <CircularProgress size={20} color='inherit' />
            ) : (
              'Analyze Document'
            )}
          </button>
        </div>
        {/* Inside the card, whose click handler stops propagation, so
            dismissing this does not also close the upload modal. */}
        <InsufficientCredits
          teamId={teamId}
          type='analysis'
          requiredCredits={1}
          isOpen={showInsufficientCredits}
          onClose={() => setShowInsufficientCredits(false)}
          isAnalyzer={true}
          isTrialing={creditSpendMode === 'trial'}
        />
      </div>
    </div>
  );
};

export default RunAesAnalysisModal;
